import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { NotificationService } from '../services/notification.service';
import { LogoutService } from '../../features/admin/auth/services/logout.service';

/**
 * Interceptor global para manejo de errores HTTP
 * Captura todos los errores de peticiones HTTP y los procesa de forma centralizada
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
    const notificationService = inject(NotificationService);
    const router = inject(Router);
    // Inyectamos el Injector de forma síncrona para poder resolver dependencias de forma dinámica
    const injector = inject(Injector);

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            let errorMessage = 'Ha ocurrido un error inesperado';

            // ============================================
            // 🔴 ERRORES DEL CLIENTE (4xx)
            // ============================================
            if (error.error instanceof ErrorEvent) {
                // Error del lado del cliente (red, etc.)
                errorMessage = `Error de red: ${error.error.message}`;
                console.error('❌ Error del cliente:', error.error.message);
            } else {
                // Error del lado del servidor
                switch (error.status) {
                    case 0:
                        // Sin conexión al servidor
                        errorMessage = 'No se puede conectar al servidor. Verifica tu conexión a internet.';
                        console.error('❌ Error de conexión: El servidor no responde');
                        break;

                    case 400:
                        // Bad Request
                        errorMessage = error.error?.error || error.error?.msg || error.error?.message || 'Solicitud inválida';
                        console.warn('⚠️ Bad Request (400):', errorMessage);
                        break;

                    case 401:
                        // No autorizado - Token inválido o expirado

                        // Si el error viene del login, es credenciales incorrectas, NO sesión expirada
                        if (req.url.includes('/login')) {
                            errorMessage = error.error?.msg || error.error?.message || 'Credenciales inválidas';
                            console.warn('⚠️ Login fallido (401):', errorMessage);
                        } else {
                            errorMessage = 'Tu sesión ha expirado o el token es inválido. Por favor, inicia sesión nuevamente.';
                            console.warn('⚠️ No autorizado (401): Token inválido o expirado');

                            // Limpiar sesión COMPLETAMENTE usando el LogoutService
                            try {
                                injector.get(LogoutService).logout(false);
                            } catch (e) {
                                // Fallback if circular dependency
                                localStorage.removeItem('token');
                                router.navigate(['/login']);
                            }
                        }
                        break;

                    case 403:
                        // Prohibido - Sin permisos
                        errorMessage = 'No tienes permisos para realizar esta acción.';
                        console.warn('⚠️ Prohibido (403): Sin permisos');
                        router.navigate(['/acceso-denegado']);
                        break;

                    case 404:
                        // No encontrado
                        errorMessage = error.error?.msg || 'Recurso no encontrado';
                        console.warn('⚠️ No encontrado (404):', errorMessage);
                        break;

                    case 409:
                        // Conflicto (ej: usuario ya existe)
                        errorMessage = error.error?.msg || 'Ya existe un registro con esos datos';
                        console.warn('⚠️ Conflicto (409):', errorMessage);
                        break;

                    case 422:
                        // Validación fallida
                        errorMessage = error.error?.msg || 'Los datos enviados no son válidos';
                        console.warn('⚠️ Validación fallida (422):', errorMessage);
                        break;

                    case 429:
                        // Demasiadas peticiones
                        errorMessage = 'Has realizado demasiadas peticiones. Intenta de nuevo en unos minutos.';
                        console.warn('⚠️ Rate limit (429): Demasiadas peticiones');
                        break;

                    case 500:
                        // Error interno del servidor
                        errorMessage = 'Error interno del servidor. Intenta de nuevo más tarde.';
                        console.error('❌ Error del servidor (500):', error.error);
                        break;

                    case 502:
                    case 503:
                    case 504:
                        // Servidor no disponible
                        errorMessage = 'El servidor no está disponible en este momento. Intenta más tarde.';
                        console.error('❌ Servidor no disponible:', error.status);
                        break;

                    default:
                        // Otros errores
                        errorMessage = error.error?.msg || error.error?.message || `Error del servidor (${error.status})`;
                        console.error('❌ Error HTTP:', error.status, error.error);
                }
            }

            // Mostrar notificación al usuario (excepto para 401 que ya redirige)
            if (error.status !== 401) {
                notificationService.showError(errorMessage);
            }

            // Re-lanzar el error para que los componentes puedan manejarlo si es necesario
            return throwError(() => ({
                status: error.status,
                message: errorMessage,
                originalError: error
            }));
        })
    );
};
